import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Modal, TextInput } from '@mantine/core';
import i18n from '@/utils/i18n';
import { ItemLocationDetailFragment } from '../graphql/fragments/itemLocationDetail.generated';
import { useSearchItemLocationsLazyQuery } from '../graphql/queries/searchItemLocations.generated';
import { useCreateItemLocationMutation } from '../graphql/mutations/createItemLocation.generated';
import { FaPlus, FaRegStar, FaStar } from 'react-icons/fa';
import ClassicLoadingSpinner from '../components/LoadingSpinner/ClassicLoadingSpinner';
import { Button, Input, Table } from 'reactstrap';
import { ContentSection } from '../components/Layout/ContentSection';
import CustomPagination from '../components/CustomPagination';
import { SearchInput } from '../components/SearchInput';
import { EventFilter } from '../components/EventFilter';
import { TypeFilter } from '../components/TypeFilter';
import debounce from 'just-debounce-it';
import { useGetItemLocationTypesQuery } from '../graphql/queries/getItemLocationTypes.generated';
import { ViewerContext } from '../providers/ViewerContextProvider';
import { Role } from '../contants';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const MAX_ELEMENTS_PER_PAGE = 10;

const FAVOURITES_KEY = 'ItemLocationFavourites';

function ItemLocationSearchView() {
  useDocumentTitle('PageTitles:itemLocationSearch');

  const favouriteItemLocations = JSON.parse(
    localStorage.getItem(FAVOURITES_KEY) || '[]',
  );

  const context = useContext(ViewerContext);
  const readOnly = context.role === Role.eventCleaningEmployee;

  const [favourites, setFavourites] = useState<string[]>(
    favouriteItemLocations,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemLocationTypeSearch, setItemLocationTypeSearch] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newItemLocationName, setNewItemLocationName] = useState('');
  const [itemLocationType, setItemLocationType] = useState('');
  const [searchResults, setSearchResults] = useState<
    ItemLocationDetailFragment[]
  >([]);

  const [query, { loading }] = useSearchItemLocationsLazyQuery();

  const { data: itemLocationTypesResponse, loading: itemLocationTypesLoading } =
    useGetItemLocationTypesQuery();

  const fetchResults = useCallback(
    debounce(
      async (
        text: string,
        itemLocationTypeString: string | null,
        eventId: string | null,
        includeDisabled: boolean,
      ) => {
        const { data } = await query({
          errorPolicy: 'all',
          fetchPolicy: 'no-cache',
          variables: {
            search_term: text,
            type:
              itemLocationTypeString !== 'all'
                ? itemLocationTypeString
                : undefined,
            event_id: eventId !== 'all' ? eventId : undefined,
            include_disabled: includeDisabled || undefined,
          },
        });

        if (data == null) {
          return;
        }

        setSearchResults(data.searchItemLocations);
      },
      200,
    ),
    [],
  );

  useEffect(() => {
    if (searchTerm.length === 0) {
      fetchResults.cancel();
      fetchResults('', itemLocationTypeSearch, selectedEvent, showDisabled);
      return;
    }

    fetchResults.cancel();
    fetchResults(
      searchTerm,
      itemLocationTypeSearch,
      selectedEvent,
      showDisabled,
    );
  }, [searchTerm, query, itemLocationTypeSearch, selectedEvent, showDisabled]);

  useEffect(() => {
    fetchResults('', itemLocationTypeSearch, selectedEvent, showDisabled);
  }, []);

  useEffect(() => {
    localStorage.setItem(FAVOURITES_KEY, JSON.stringify(favourites));
  }, [favourites]);

  const [createItemLocation, { loading: creating }] =
    useCreateItemLocationMutation({
      onCompleted: (d) => {
        setIsModalOpen(false);
        setSearchTerm(d.createItemLocation.name);
      },
    });

  const itemLocationTypes = itemLocationTypesResponse?.getItemLocationTypes;

  const { favourites: favouriteSearchResults, normal: normalSearchResults } =
    searchResults.reduce(
      (
        acc: {
          favourites: ItemLocationDetailFragment[];
          normal: ItemLocationDetailFragment[];
        },
        cur,
      ) => {
        if (favourites.includes(cur.id)) {
          acc.favourites.push(cur);
        } else {
          acc.normal.push(cur);
        }

        return acc;
      },
      {
        favourites: [],
        normal: [],
      },
    );

  const favouritesOffset = favouriteSearchResults.length;
  const normalOffset = normalSearchResults.length;

  favouriteSearchResults.sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();

    return aName > bName ? 1 : bName > aName ? -1 : 0;
  });

  normalSearchResults.sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();

    return aName > bName ? 1 : bName > aName ? -1 : 0;
  });

  const itemLocationsPaginationSlice = [
    ...favouriteSearchResults,
    ...normalSearchResults,
  ].slice(
    MAX_ELEMENTS_PER_PAGE * (currentPage - 1),
    MAX_ELEMENTS_PER_PAGE * (currentPage - 1) + MAX_ELEMENTS_PER_PAGE,
  );

  const maxItemLocationCount = Math.ceil(
    (favouritesOffset + normalOffset) / MAX_ELEMENTS_PER_PAGE,
  );

  return (
    <ContentSection
      title={i18n.t('ItemLocation:itemLocationSearch')}
      collapsable={false}
    >
      {!readOnly && (
        <Button
          className="float-end mb-3"
          size="sm"
          color="primary"
          onClick={() => setIsModalOpen(true)}
          disabled={creating}
        >
          <FaPlus className="me-1" />
          {i18n.t('Common:add')}
        </Button>
      )}
      <SearchInput
        paramName="search"
        placeholder={i18n.t('ItemLocation:searchPlaceholder')}
        label={i18n.t('Common:searchTerm')}
        className="mb-3"
        onChange={(value) => setSearchTerm(value)}
        initialValue={searchTerm}
      />

      <TypeFilter
        paramName="itemLocationType"
        label={i18n.t('Common:type')}
        className="mb-3"
        types={itemLocationTypes}
        loading={itemLocationTypesLoading}
        onChange={(value) => setItemLocationTypeSearch(value)}
        initialValue={itemLocationTypeSearch}
        showAll={true}
        allOptionValue="all"
      />

      <EventFilter
        paramName="eventId"
        label={i18n.t('Common:event')}
        className="mb-3"
        onChange={(value) => setSelectedEvent(value)}
        initialValue={selectedEvent}
        useMantineSelect={true}
        searchable={true}
        clearable={true}
        showAll={true}
        readOnly={readOnly}
      />

      <div className="form-check mb-3">
        <Input
          type="checkbox"
          className="form-check-input"
          id="showDisabledItemLocations"
          checked={showDisabled}
          onChange={(e) => setShowDisabled(e.currentTarget.checked)}
        />
        <label className="form-check-label" htmlFor="showDisabledItemLocations">
          {i18n.t('ItemLocation:showDisabled')}
        </label>
      </div>

      {loading && <ClassicLoadingSpinner />}
      {!loading && searchResults.length === 0 && (
        <p>{i18n.t('ItemLocation:noResults')}</p>
      )}
      {!loading && searchResults.length > 0 && (
        <>
          <Table striped hover bordered>
            <thead>
              <tr>
                <th>{i18n.t('Common:name')}</th>
                <th>{i18n.t('Common:type')}</th>
                <th>{i18n.t('Common:event')}</th>
                <th>{i18n.t('ItemLocation:eventAccount')}</th>
                <th>{i18n.t('Common:favourites')}</th>
              </tr>
            </thead>
            <tbody>
              {itemLocationsPaginationSlice.map((result) => {
                const isFavourite = favourites.includes(result.id);

                return (
                  <tr key={result.id}>
                    <td>
                      <Link to={Routes.itemLocation(result.id)}>
                        {result.name}
                      </Link>
                      {result.disabled_at != null && (
                        <span
                          className="badge bg-secondary ms-2"
                          title={new Date(result.disabled_at).toLocaleString()}
                        >
                          {i18n.t('ItemLocation:disabled')}
                        </span>
                      )}
                    </td>
                    <td>{result.type || '-'}</td>
                    <td>
                      {!result.event && '-'}
                      {result.event && (
                        <Link to={Routes.eventDetail(result.event?.id || '')}>
                          {result.event?.name}
                        </Link>
                      )}
                    </td>
                    <td>
                      {!result.event_account && '-'}
                      {result.event_account && (
                        <Link
                          to={Routes.eventAccountDetail(
                            result.event_account?.id || '',
                          )}
                        >
                          {result.event_account?.name}
                        </Link>
                      )}
                    </td>
                    <td className="d-flex justify-content-center align-items-center">
                      <div>
                        {isFavourite && (
                          <FaStar
                            style={{
                              height: 20,
                              width: 20,
                              color: '#FFA534',
                            }}
                            onClick={() => {
                              const currentId = result.id;

                              const newFavourites = favourites.filter((f) => {
                                return f !== currentId;
                              });

                              setFavourites(newFavourites);
                            }}
                          />
                        )}
                        {!isFavourite && (
                          <FaRegStar
                            style={{ height: 20, width: 20 }}
                            onClick={() => {
                              const currentId = result.id;

                              setFavourites([...favourites, currentId]);
                            }}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {maxItemLocationCount > 0 && (
            <CustomPagination
              currentPage={currentPage}
              pageCount={maxItemLocationCount}
              onPageChange={(page: number) => {
                setCurrentPage(page);
              }}
            />
          )}
        </>
      )}

      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={i18n.t('ItemLocation:addModalTitle')}
      >
        <TextInput
          label={i18n.t('Common:name')}
          value={newItemLocationName}
          onChange={(event) =>
            setNewItemLocationName(event.currentTarget.value)
          }
        />
        <br />
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:type')}
          </span>
          <Input
            type="select"
            value={itemLocationType}
            onChange={(e) => {
              setItemLocationType(e.currentTarget.value);
            }}
            disabled={itemLocationTypesLoading}
          >
            {itemLocationTypes?.map(({ type }) => {
              return <option>{type}</option>;
            })}
          </Input>
        </div>
        <Button
          className="mt-3"
          onClick={() => {
            createItemLocation({
              variables: {
                name: newItemLocationName,
                type: itemLocationType || 'Event',
              },
            });
            setIsModalOpen(false);
            setNewItemLocationName('');
          }}
        >
          {i18n.t('Common:add')}
        </Button>
      </Modal>
    </ContentSection>
  );
}

export default ItemLocationSearchView;
